import { ReportDatasetKind, ReportValueType } from "@ibada/shared-types/reporting"

import {
  JournalLineDirection,
  type AccountingAccountClassification,
  type AccountingAccountType,
  type ChartAccount,
  type JournalEntryStatus,
  type JournalEntryView,
  type LedgerResponse,
  type MoneyText,
  type NormalBalance,
  type StatementSection as StatementSectionData,
  type StatementsResponse,
  type TrialBalanceRow,
} from "@/features/finance/model/accounting-reports-types"

import type { NormalizedReport, ReportColumn, ReportRow } from "@/features/reports/model/normalized-report"
import { StatementSection } from "@/features/reports/model/report-registry-types"

/**
 * Adapters turning the ad-hoc /finance/accounting/* payloads into the engine's `ReportResult` shape, so the
 * same shell (SummaryCards + ReportTable) renders them without knowing they aren't engine reports. Statements
 * and journal don't fit columns/rows and use custom viewers instead.
 */

function col(key: string, labelKey: string, valueType: ReportValueType): ReportColumn {
  return { key, labelKey, source: key, valueType, sortable: true, exportable: true }
}

function meta(endpoint: string): NormalizedReport["meta"] {
  return {
    executionId: `accounting:${endpoint}`,
    baseEntity: "accounting",
    datasetKind: ReportDatasetKind.DETAIL,
    grainLabelKey: "finance.reports.grain.row",
  }
}

export function adaptTrialBalance(data: {
  rows: TrialBalanceRow[]
  /** The SERVER's column totals, as canonical decimal strings — the browser does not re-add the columns. */
  totalDebit: MoneyText
  totalCredit: MoneyText
  balanced: boolean
}): NormalizedReport {
  return {
    meta: meta("trial-balance"),
    columns: [
      col("code", "finance.reports.columns.code", ReportValueType.TEXT),
      col("name", "finance.reports.columns.account", ReportValueType.TEXT),
      col("debit", "finance.reports.columns.debit", ReportValueType.CURRENCY),
      col("credit", "finance.reports.columns.credit", ReportValueType.CURRENCY),
      col("balance", "finance.reports.columns.balance", ReportValueType.CURRENCY),
    ],
    rows: data.rows.map((row) => ({
      // Carried for the balance drill-down; no column, so it never renders.
      accountingAccountId: row.id,
      code: row.code,
      name: row.name,
      debit: row.debit,
      credit: row.credit,
      balance: row.balance,
    })),
    summaryCards: [
      { key: "totalDebit", labelKey: "finance.reports.columns.debit", value: data.totalDebit, valueType: ReportValueType.CURRENCY },
      { key: "totalCredit", labelKey: "finance.reports.columns.credit", value: data.totalCredit, valueType: ReportValueType.CURRENCY },
      { key: "balanced", labelKey: "finance.reports.viewerSummary.balanced", value: data.balanced, valueType: ReportValueType.BOOLEAN },
    ],
  }
}

export function adaptLedger(data: LedgerResponse, labels: AccountingLabels): NormalizedReport {
  return {
    meta: meta("ledger"),
    columns: [
      col("date", "finance.reports.columns.date", ReportValueType.DATE),
      col("reference", "finance.reports.columns.reference", ReportValueType.TEXT),
      col("debit", "finance.reports.columns.debit", ReportValueType.CURRENCY),
      col("credit", "finance.reports.columns.credit", ReportValueType.CURRENCY),
      col("runningBalance", "finance.reports.columns.runningBalance", ReportValueType.CURRENCY),
    ],
    rows: data.lines.map((line) => ({
      // Carried for the drill-down into the transaction's audit; no column, so it never renders.
      transactionId: line.transactionId,
      date: line.entryDate,
      // Not every line HAS a transaction reference — an opening balance has none. Printing "—" left a treasurer
      // staring at an unexplained debit in their bank account; the entry always knows what it is, so say it.
      reference: line.transactionReference ?? labels.source(line.sourceType),
      debit: line.direction === JournalLineDirection.Debit ? line.amount : null,
      credit: line.direction === JournalLineDirection.Credit ? line.amount : null,
      runningBalance: line.runningBalance,
    })),
    summaryCards: [
      { key: "opening", labelKey: "finance.reports.columns.opening", value: data.openingBalance, valueType: ReportValueType.CURRENCY },
      { key: "closing", labelKey: "finance.reports.columns.closing", value: data.closingBalance, valueType: ReportValueType.CURRENCY },
    ],
  }
}

/**
 * Resolves an accounting enum value to its display label. The report table renders SELECT cells as-is,
 * so the enum has to arrive already translated or it shows up raw ("NET_ASSET", "DEBIT").
 */
export type AccountingLabels = {
  type: (value: AccountingAccountType) => string
  classification: (value: AccountingAccountClassification) => string
  direction: (value: NormalBalance) => string
  /** Journal entry status (POSTED / REVERSED). */
  entryStatus: (value: JournalEntryStatus) => string
  /** Journal entry source (FINANCE_TRANSACTION / OPENING / ADJUSTMENT / TRANSFER) — what produced the line. */
  source: (value: string) => string
}

/**
 * Flattens journal ENTRIES into one row per POSTING (an entry's debit and credit lines are separate rows), which
 * is the shape a journal has to take in a spreadsheet. The on-screen viewer keeps its entry-card layout — this
 * adapter exists so the CSV export can reuse the shared column/format/i18n pipeline instead of a bespoke writer.
 */
export function adaptJournal(entries: JournalEntryView[], labels: AccountingLabels): NormalizedReport {
  const rows: ReportRow[] = []
  for (const entry of entries) {
    for (const line of entry.lines) {
      rows.push({
        date: entry.entryDate,
        // An entry need not carry a transaction reference (an opening balance has none). A raw journal id is an
        // implementation handle, not an accounting reference; display the source that produced the entry.
        reference: entry.transactionReference ?? labels.source(entry.sourceType),
        status: labels.entryStatus(entry.status),
        source: labels.source(entry.sourceType),
        code: line.accountCode,
        name: line.accountName,
        debit: line.direction === JournalLineDirection.Debit ? line.amount : null,
        credit: line.direction === JournalLineDirection.Credit ? line.amount : null,
      })
    }
  }

  return {
    meta: meta("journal"),
    columns: [
      col("date", "finance.reports.columns.date", ReportValueType.DATE),
      col("reference", "finance.reports.columns.reference", ReportValueType.TEXT),
      col("status", "finance.reports.columns.status", ReportValueType.SELECT),
      col("source", "finance.reports.columns.source", ReportValueType.SELECT),
      col("code", "finance.reports.columns.code", ReportValueType.TEXT),
      col("name", "finance.reports.columns.account", ReportValueType.TEXT),
      col("debit", "finance.reports.columns.debit", ReportValueType.CURRENCY),
      col("credit", "finance.reports.columns.credit", ReportValueType.CURRENCY),
    ],
    rows,
  }
}

export function adaptChartOfAccounts(
  data: { items: ChartAccount[] },
  labels: AccountingLabels,
): NormalizedReport {
  return {
    meta: meta("chart"),
    columns: [
      col("code", "finance.reports.columns.code", ReportValueType.TEXT),
      col("name", "finance.reports.columns.account", ReportValueType.TEXT),
      col("type", "finance.reports.columns.type", ReportValueType.SELECT),
      col("classification", "finance.reports.columns.classification", ReportValueType.SELECT),
      col("normalBalance", "finance.reports.columns.normalBalance", ReportValueType.SELECT),
      col("balance", "finance.reports.columns.balance", ReportValueType.CURRENCY),
    ],
    rows: data.items.map((item) => ({
      // Carried for the balance drill-down; there is no column for it, so it is never rendered.
      accountingAccountId: item.id,
      code: item.code,
      name: item.name,
      type: labels.type(item.type),
      classification: labels.classification(item.classification),
      normalBalance: labels.direction(item.normalBalance),
      balance: item.balance,
    })),
  }
}

/**
 * Flatten a financial statement (Balance Sheet / Income Statement / Cash Flow) into a line/amount table so it
 * can be EXPORTED (the on-screen viewer stays the grouped custom cards). Each section's line items are listed,
 * then a "{Section} — Total" row; the money stays a canonical decimal string (IBA-116), never converted.
 */
export function adaptStatement(
  data: StatementsResponse,
  section: StatementSection,
  t: (key: string) => string,
): NormalizedReport {
  const columns: ReportColumn[] = [
    col("line", "finance.reports.columns.account", ReportValueType.TEXT),
    col("amount", "finance.reports.columns.balance", ReportValueType.CURRENCY),
  ]
  const rows: ReportRow[] = []
  const s = (key: string) => t(`finance.reports.statements.${key}`)
  const totalWord = s("total")
  const pushSection = (label: string, sec: StatementSectionData) => {
    sec.items.forEach((item) => rows.push({ line: item.name, amount: item.amount }))
    rows.push({ line: `${label} — ${totalWord}`, amount: sec.total })
  }

  if (section === StatementSection.BalanceSheet) {
    pushSection(s("assets"), data.balanceSheet.assets)
    pushSection(s("liabilities"), data.balanceSheet.liabilities)
    pushSection(s("netAssets"), data.balanceSheet.netAssets)
    return { meta: meta("balance-sheet"), columns, rows }
  }
  if (section === StatementSection.IncomeStatement) {
    pushSection(s("income"), data.incomeStatement.income)
    pushSection(s("expenses"), data.incomeStatement.expenses)
    rows.push({ line: s("surplus"), amount: data.incomeStatement.surplus })
    return { meta: meta("income-statement"), columns, rows }
  }
  rows.push({ line: s("openingCash"), amount: data.cashFlow.openingCash })
  rows.push({ line: s("inflows"), amount: data.cashFlow.inflows })
  rows.push({ line: s("outflows"), amount: data.cashFlow.outflows })
  rows.push({ line: s("netChange"), amount: data.cashFlow.netChange })
  rows.push({ line: s("closingCash"), amount: data.cashFlow.closingCash })
  return { meta: meta("cash-flow"), columns, rows }
}
